"use client";

import { useQuery } from "@tanstack/react-query";
import { useLocale } from "next-intl";

import type { MealPlanDraftView } from "@/lib/calendar/meal-plan-draft";
import { localToday } from "@/lib/calendar/week";

/** Late in the evening the draft starts tomorrow, as the suggest sheet does. */
const LATE_HOUR = 21;

/** This week's free meal plan draft for the empty dashboard; null when there is none to show. */
export function useMealPlanDraft(enabled: boolean) {
  const locale = useLocale();
  const today = localToday();

  return useQuery<MealPlanDraftView | null>({
    queryKey: ["mealPlanDraft", locale, today],
    queryFn: async () => {
      const skipToday = new Date().getHours() >= LATE_HOUR;
      const res = await fetch(
        `/api/meal-plan-draft?today=${today}&skipToday=${skipToday}&locale=${locale}`
      );
      // A failed load is an error, not "no draft": reloading after a failed save then keeps the screen as it is.
      if (!res.ok) throw new Error(`Meal plan draft: ${res.status}`);
      const data: { draft: MealPlanDraftView | null } = await res.json();
      return data.draft;
    },
    enabled,
    // The server already answers "no draft" when making one fails; asking again would only delay the empty state.
    retry: false,
    staleTime: 5 * 60 * 1000,
    // The screen edits this cache while saves are on their way; a refetch then could show an older list.
    refetchOnWindowFocus: false,
  });
}
